import {
    Avatar, Box, CircularProgress, FormControl, IconButton,
    MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography
} from "@mui/material"
import { AccountBalanceOutlined, PhoneIphoneOutlined, Refresh } from "@mui/icons-material"
import { FC, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import { Apis, numberWithCommas } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"

interface PaymentRow {
    _id: string
    title: string
    status: 'Pending' | 'Completed'
    propertyName?: string
    propertyLogo?: any
    tenant?: { name: string; profilePhotos?: any }
    payment: {
        dateOfPayment: string
        referenceNumber: string
        amount: number
        method: string
    }
}

type StatusFilter = 'Pending' | 'Completed' | 'all'

interface StateProps {
    payments: PaymentRow[]
    loading: boolean
    refreshing: boolean
    status: StatusFilter
    page: number
    pageSize: number
    count: number
    sortOrder: 'asc' | 'desc'
}

const AdminPayments: FC = () => {
    const [state, setState] = useState<StateProps>({
        payments: [],
        loading: true,
        refreshing: false,
        status: 'all',
        page: 0,
        pageSize: 25,
        count: 0,
        sortOrder: 'desc'
    })

    const { payments, loading, refreshing, status, page, pageSize, count, sortOrder } = state

    const fetchPayments = ({ newStatus, newPage, newPageSize, newSortOrder, processVariable }: any) => {
        const Status = newStatus || state.status
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        const SortOrder = newSortOrder || state.sortOrder
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, status: Status, page: Page, pageSize: PageSize, sortOrder: SortOrder }
        Apis({ module: 'Payments', action: 'getAllPayments', status: Status, page: Page, pageSize: PageSize, sortOrder: SortOrder })
            .then((resp: any) => setState(s => ({ ...s, payments: resp.data || [], count: resp.count ?? s.count, ...afterLoad })))
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    const toggleSort = () => fetchPayments({ newPage: 0, newSortOrder: sortOrder === 'desc' ? 'asc' : 'desc', processVariable: 'loading' })

    useEffect(() => {
        fetchPayments({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="Payments" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {count > 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {count.toLocaleString()} total
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={status}
                            onChange={(e: SelectChangeEvent) => fetchPayments({ newStatus: e.target.value as StatusFilter, newPage: 0, processVariable: 'loading' })}
                            sx={{ fontSize: 13, borderRadius: 2 }}
                        >
                            <MenuItem sx={{ fontSize: 13 }} value="all">All Statuses</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Pending">Pending</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Completed">Completed</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchPayments({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!payments.length || loading ?
                <NoData message="No payments found." loading={loading} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Tenant</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Property</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>
                                        <TableSortLabel active direction={sortOrder} onClick={toggleSort}>
                                            Date
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Method</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map(p => {
                                    const isBank = (p.payment?.method || '').toLowerCase().includes('bank')
                                    return (
                                        <TableRow hover key={p._id}>
                                            <TableCell>
                                                <Avatar src={p.tenant?.profilePhotos?.small?.url} sx={{ width: 28, height: 28, fontSize: 12 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{p.tenant?.name || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{p.propertyName?.trim() || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                {p.payment?.dateOfPayment
                                                    ? new Date(p.payment.dateOfPayment).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })
                                                    : '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                    {isBank
                                                        ? <AccountBalanceOutlined sx={{ fontSize: 17, color: '#1565c0' }} />
                                                        : <PhoneIphoneOutlined sx={{ fontSize: 17, color: '#00838f' }} />}
                                                    {p.payment?.method || '—'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{p.payment?.referenceNumber || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(p.payment?.amount || 0)}</TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchPayments({ newPage, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchPayments({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }
        </Box>
    )
}

export default AdminPayments
